import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, map, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TimeSheetService } from '../../../time-sheet/services/time-sheet.service';
import { CompaniesService } from '../../../services/companies.service';
import { TimerDepartmentGroup, findTimerDepartment, loadTimerDepartmentGroups } from '../../helpers/timer-department-groups';
import { NoteMentionsService } from '../../../corporate/meeting-notes/services/note-mentions.service';
import { CompanyUserDTO } from '../../../models/company/company-user-dto.model';

export interface TimerStopDialogData {
  /** Note/timer label shown for context (read-only). */
  label?: string;
  /** Secondary label, e.g. reference number (read-only). */
  reference?: string;
  /** Elapsed time label, e.g. "00:12:30" (read-only). */
  elapsedLabel?: string;
  /** Initial billable value. */
  billable?: boolean;
  /** Initial description. */
  description?: string;
  /**
   * When true, the note has no department/service (a service-less note) — a timesheet row
   * still requires them, so the dialog shows required Department + Service pickers.
   */
  needsDepartmentService?: boolean;
  /** Organisation the note belongs to; its departments are offered alongside the viewing org's. */
  noteCompanyId?: number | null;
  /** The note's client form and thread, so attendees can be offered from the note's people. */
  formId?: number | null;
  noteId?: number | null;
}

export interface TimerStopDialogResult {
  description: string;
  billable: boolean;
  /** Selected attendee azureObjectIds. The current user is added automatically by the backend. */
  attendees: string[];
  /** Chosen department — only set when the dialog collected it (service-less notes). */
  departmentId?: number;
  /** Chosen service — only set when the dialog collected it (service-less notes). */
  serviceId?: number;
}

interface AttendeeOption {
  id: string;
  name: string;
}

/**
 * Confirmation dialog shown when stopping a timer. Lets the user edit the
 * description, toggle billable, and pick additional attendees (org users) — the
 * backend creates a timesheet entry for the creator AND each attendee.
 */
@Component({
    selector: 'app-timer-stop-dialog',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './timer-stop-dialog.component.html',
    styleUrls: ['./timer-stop-dialog.component.scss']
})
export class TimerStopDialogComponent implements OnInit {
  readonly data: TimerStopDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<TimerStopDialogComponent>);
  private readonly timeSheetService = inject(TimeSheetService);
  private readonly companiesService = inject(CompaniesService);
  private readonly noteMentions = inject(NoteMentionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = new FormGroup({
    description: new FormControl(this.data.description || ''),
    billable: new FormControl(this.data.billable ?? true),
    attendees: new FormControl<string[]>([]),
    departmentId: new FormControl<number | null>(null),
    serviceId: new FormControl<number | null>(null),
  });

  attendeeOptions: AttendeeOption[] = [];
  loadingUsers = true;

  readonly needsDepartmentService = !!this.data.needsDepartmentService;
  /** The viewing organisation's departments and, for another organisation's note, that one's too. */
  departmentGroups: TimerDepartmentGroup[] = [];
  services: { id: number; description: string }[] = [];
  loadingDepartments = false;

  ngOnInit(): void {
    // A note timer offers the note's people across organisations (its org, every org holding the
    // client, guests), minus anyone a confidential note hides from; anything else, or a failed
    // lookup, falls back to the viewing org's users.
    const companyUsers$ = this.timeSheetService.getCompanyUsers(this.companiesService.managedCompanyId).pipe(
      map(res => (res?.users || []) as CompanyUserDTO[]),
      catchError(() => of([] as CompanyUserDTO[])),
    );
    const users$ = this.data.formId
      ? this.noteMentions.forNote(this.data.formId, this.data.noteId, this.data.noteCompanyId).pipe(
          switchMap(users => users
            ? of(users.filter(u => u.canSeeNote !== false) as CompanyUserDTO[])
            : companyUsers$))
      : companyUsers$;

    users$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(users => {
      this.attendeeOptions = users
        .filter(u => !!u.azureObjectId)
        .map(u => ({
          id: u.azureObjectId,
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      this.loadingUsers = false;
    });

    if (this.needsDepartmentService) {
      this.loadingDepartments = true;
      loadTimerDepartmentGroups(this.companiesService, this.data.noteCompanyId).pipe(
        catchError(() => of([] as TimerDepartmentGroup[])),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(groups => {
        this.departmentGroups = groups
          .map(g => ({ ...g, departments: g.departments.filter(d => d.name !== 'Leave') }))
          .filter(g => g.departments.length > 0);
        this.loadingDepartments = false;
      });
    }
  }

  onDepartmentChange(deptId: number): void {
    this.form.get('serviceId').setValue(null);
    const dep = findTimerDepartment(this.departmentGroups, deptId);
    this.services = (dep?.services?.map(s => ({ id: s.id, description: s.description })) || [])
      .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''));
  }

  save(): void {
    if (this.needsDepartmentService && (!this.form.value.departmentId || !this.form.value.serviceId)) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.value;
    const result: TimerStopDialogResult = {
      description: (v.description || '').trim(),
      billable: !!v.billable,
      attendees: v.attendees || [],
      departmentId: v.departmentId ?? undefined,
      serviceId: v.serviceId ?? undefined,
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
